import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import {
  FaBook,
  FaFilter,
  FaCalendarAlt,
  FaBalanceScale,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSearch
} from "react-icons/fa";

export default function Ledger() {
  const [ledgerLines, setLedgerLines] = useState([]);
  const [summary, setSummary] = useState({ total_debits: 0, total_credits: 0, is_balanced: true });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialAccountId = queryParams.get("accountId") || "";

  const [filterAccount, setFilterAccount] = useState(initialAccountId);
  const [filterType, setFilterType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchAccountsList = () => {
    axios.get("/api/admin/accounting/chart-of-accounts").then((res) => {
      if (res.data.status === 200) {
        setAccounts(res.data.accounts || []);
      }
    }).catch(() => {});
  };

  const fetchLedger = () => {
    setLoading(true);
    const params = {};
    if (filterAccount) params.account_id = filterAccount;
    if (filterType) params.type = filterType;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    axios
      .get("/api/admin/accounting/ledger", { params })
      .then((res) => {
        if (res.data.status === 200) {
          setLedgerLines(res.data.ledger?.data || res.data.ledger || []);
          if (res.data.summary) {
            setSummary(res.data.summary);
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    document.title = "General Ledger — Admin";
    fetchAccountsList();
    fetchLedger();
  }, [filterAccount, filterType, startDate, endDate]);

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: "var(--admin-text-main)" }}>
            General Accounting Ledger
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            Orders, payments, shipping, and refunds
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/chart_of_account"
            className="btn btn-outline-dark rounded-pill px-3 py-2 fw-semibold"
            style={{ fontSize: "13.5px" }}
          >
            Chart of Accounts
          </Link>
          <Link
            to="/admin/reports"
            className="btn btn-primary rounded-pill px-4 py-2 fw-semibold d-flex align-items-center gap-2"
            style={{ fontSize: "13.5px", background: "var(--admin-primary)", border: "none" }}
          >
            <FaBalanceScale /> Financial Statements
          </Link>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-4">
          <div className="admin-card p-3 mb-0 bg-white">
            <span className="text-muted fw-semibold d-block mb-1" style={{ fontSize: "12px" }}>
              TOTAL DEBITS (DR)
            </span>
            <h4 className="fw-bold mb-0 text-success">
              Rs. {Number(summary.total_debits || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h4>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div className="admin-card p-3 mb-0 bg-white">
            <span className="text-muted fw-semibold d-block mb-1" style={{ fontSize: "12px" }}>
              TOTAL CREDITS (CR)
            </span>
            <h4 className="fw-bold mb-0 text-danger">
              Rs. {Number(summary.total_credits || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h4>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div className="admin-card p-3 mb-0 bg-white">
            <span className="text-muted fw-semibold d-block mb-1" style={{ fontSize: "12px" }}>
              DOUBLE-ENTRY INTEGRITY
            </span>
            <div className="d-flex align-items-center gap-2 mt-1">
              {summary.is_balanced ? (
                <>
                  <FaCheckCircle color="var(--success)" size={18} />
                  <span className="fw-bold text-success" style={{ fontSize: "15px" }}>
                    Strictly Balanced (DR = CR)
                  </span>
                </>
              ) : (
                <>
                  <FaExclamationTriangle color="var(--danger)" size={18} />
                  <span className="fw-bold text-danger" style={{ fontSize: "15px" }}>
                    Variance Detected!
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="admin-card mb-4 p-3 bg-white">
        <div className="row g-3 align-items-center">
          <div className="col-12 col-md-4">
            <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "11.5px" }}>
              ACCOUNT FILTER
            </label>
            <select
              className="form-select form-select-sm"
              value={filterAccount}
              onChange={(e) => setFilterAccount(e.target.value)}
            >
              <option value="">All Ledger Accounts</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.account_code} — {a.account_name} ({a.account_type})
                </option>
              ))}
            </select>
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "11.5px" }}>
              TRANSACTION TYPE
            </label>
            <select
              className="form-select form-select-sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Debits & Credits</option>
              <option value="DEBIT">Debits Only</option>
              <option value="CREDIT">Credits Only</option>
            </select>
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "11.5px" }}>
              FROM DATE
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label text-muted fw-semibold mb-1" style={{ fontSize: "11.5px" }}>
              TO DATE
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="admin-card overflow-hidden">
        {loading ? (
          <div className="py-5 text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 fw-semibold">Loading General Ledger Entries...</p>
          </div>
        ) : ledgerLines.length === 0 ? (
          <div className="p-5 text-center">
            <FaBook size={40} className="text-muted mb-2" />
            <h6 className="fw-bold">No Ledger Entries Found</h6>
            <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
              New journal entries are posted automatically when customer orders and refunds occur.
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light text-uppercase" style={{ fontSize: "11.5px", letterSpacing: "0.03em" }}>
                <tr>
                  <th style={{ width: "130px" }}>Date</th>
                  <th>Entry Ref</th>
                  <th>Account</th>
                  <th>Description / Particulars</th>
                  <th className="text-end text-success" style={{ width: "150px" }}>Debit (DR)</th>
                  <th className="text-end text-danger" style={{ width: "150px" }}>Credit (CR)</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: "13px" }}>
                {ledgerLines.map((line) => {
                  const entry = line.journal_entry;
                  const acc = line.account;
                  return (
                    <tr key={line.id}>
                      <td className="text-muted">
                        {entry?.entry_date || new Date(line.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <code className="fw-bold text-dark">{entry?.entry_number || `JE-${line.journal_entry_id}`}</code>
                        <span className="badge bg-light text-muted border ms-2" style={{ fontSize: "10px" }}>
                          {entry?.reference_type || "manual"}
                        </span>
                      </td>
                      <td>
                        <span className="fw-bold text-dark d-block">
                          {acc?.account_code} — {acc?.account_name}
                        </span>
                        <small className="text-muted">{acc?.account_type}</small>
                      </td>
                      <td>
                        <span className="text-dark">{line.description || entry?.description}</span>
                      </td>
                      <td className="text-end fw-bold text-success fs-6">
                        {line.type === "DEBIT"
                          ? `Rs. ${Number(line.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                          : "—"}
                      </td>
                      <td className="text-end fw-bold text-danger fs-6">
                        {line.type === "CREDIT"
                          ? `Rs. ${Number(line.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
